import React from "react"
import moment from "moment"
import { Link } from "react-router-dom"
import { Badge } from "reactstrap"

const lifecycleLabels = {
  adminGrant: "Administrator grant",
  new: "New membership",
  reactivation: "Reactivation",
  renewal: "Renewal",
}

const paymentTypeLabels = {
  bankTransfer: "Bank transfer",
  creditCard: "Credit card",
  mobilePayment: "Mobile payment",
  virtualWallet: "Virtual wallet",
}

const statusMeta = {
  confirmed: { color: "success", label: "Confirmed" },
  pending: { color: "warning", label: "Pending" },
  refunded: { color: "info", label: "Refunded" },
  rejected: { color: "danger", label: "Rejected" },
}

const capitalizeFirst = value =>
  value ? value.charAt(0).toUpperCase() + value.slice(1) : ""

const formatDate = (value, format) => {
  const date = value
    ? typeof value === "string"
      ? moment(value, moment.ISO_8601, true)
      : moment(value)
    : null
  return date?.isValid() ? date.format(format) : null
}

const getProductPresentation = productName => {
  if (!productName) {
    return {
      durationLabel: "Duration not recorded",
      planLabel: "Membership",
    }
  }

  if (productName === "summerMembership") {
    return {
      durationLabel: "Duration not recorded",
      planLabel: "Summer",
    }
  }

  if (productName === "undefinedMembership") {
    return {
      durationLabel: "Duration not recorded",
      planLabel: "Free",
    }
  }

  const normalizedName = productName.replace(/Membership$/i, "")
  const [plan, duration] = normalizedName.split("-")
  const durationInMonths = Number(duration)

  return {
    durationLabel:
      Number.isFinite(durationInMonths) && durationInMonths > 0
        ? `${durationInMonths} ${
            durationInMonths === 1 ? "month" : "months"
          }`
        : "Duration not recorded",
    planLabel: capitalizeFirst(plan) || "Membership",
  }
}

const formatPerson = person => {
  const name = [person?.firstName, person?.lastName].filter(Boolean).join(" ")

  if (name && person?.userId) return `${name} (#${person.userId})`
  if (name) return name
  if (person?.userId) return `Administrator #${person.userId}`
  return "Administrator not recorded"
}

export const getMembershipPresentation = membership => {
  const product = membership?.products?.find(item => item?.name) || {}
  const productPresentation = getProductPresentation(product.name)
  const lifecycle = membership?.premiumLifecycle || {}
  const startDate = lifecycle?.entitlement?.startDate
  const endDate = lifecycle?.entitlement?.endDate || product?.validUntil
  const formattedStartDate = formatDate(startDate, "D MMM YYYY")
  const formattedEndDate = formatDate(endDate, "D MMM YYYY")
  const isAdmin =
    membership?.type === "upgradeMembershipByAdmin" ||
    lifecycle?.source === "admin" ||
    Boolean(membership?.admin?.id || membership?.admin?.userId)
  let validityLabel = "Dates not recorded"

  if (formattedStartDate && formattedEndDate) {
    validityLabel = `${formattedStartDate} → ${formattedEndDate}`
  } else if (formattedEndDate) {
    validityLabel = `Until ${formattedEndDate}`
  }

  return {
    ...productPresentation,
    amountLabel:
      membership?.amount !== undefined &&
      membership?.amount !== null &&
      Number(membership.amount) === 0
        ? "No charge"
        : membership?.amount !== undefined && membership?.amount !== null
        ? `${membership.amount} ${membership?.currency || ""}`.trim()
        : "Amount not recorded",
    lifecycleLabel:
      lifecycleLabels[lifecycle?.classification] || "Membership update",
    sourceLabel: isAdmin
      ? "Applied by administrator"
      : paymentTypeLabels[membership?.paymentType] || "Player purchase",
    validityLabel,
    validityStatus: endDate
      ? moment(endDate).isAfter(moment())
        ? "Active"
        : "Expired"
      : "Status not recorded",
  }
}

const formatTransaction = (cellContent, membership) => {
  const status =
    statusMeta[String(membership?.status || "").toLowerCase()] ||
    { color: "secondary", label: "Status not recorded" }
  const createdAt = formatDate(membership?.createdAt, "D MMM YYYY, HH:mm")

  return (
    <div style={{ minWidth: "205px" }}>
      <div className="d-flex align-items-center flex-nowrap">
        <span className="font-weight-semibold mr-2 text-nowrap">
          Payment #{membership?.paymentId || "—"}
        </span>
        <Badge
          color={status.color}
          pill
          className="px-2 py-1 font-size-11 flex-shrink-0"
        >
          {status.label}
        </Badge>
      </div>
      <div className="text-muted font-size-11 mt-1">
        {createdAt || "Date not recorded"}
      </div>
    </div>
  )
}

const formatMembership = (cellContent, membership) => {
  const presentation = getMembershipPresentation(membership)

  return (
    <div style={{ minWidth: "180px", whiteSpace: "normal" }}>
      <div className="font-weight-semibold">{presentation.planLabel}</div>
      <div className="text-muted font-size-12 mt-1">
        {presentation.durationLabel} · {presentation.lifecycleLabel}
      </div>
    </div>
  )
}

const formatValidity = (cellContent, membership) => {
  const presentation = getMembershipPresentation(membership)
  const color = presentation.validityStatus === "Active" ? "success" : "secondary"

  return (
    <div style={{ minWidth: "190px", whiteSpace: "normal" }}>
      <Badge color={color} pill className="px-2 py-1">
        {presentation.validityStatus}
      </Badge>
      <div className="text-muted font-size-12 mt-1">
        {presentation.validityLabel}
      </div>
    </div>
  )
}

const formatSource = (cellContent, membership) => {
  const presentation = getMembershipPresentation(membership)
  const adminLabel = formatPerson(membership?.admin)

  return (
    <div style={{ minWidth: "180px", whiteSpace: "normal" }}>
      <div className="font-weight-semibold">{presentation.sourceLabel}</div>
      <div className="text-muted font-size-12 mt-1">
        {presentation.amountLabel}
      </div>
      {(membership?.admin?.id || membership?.admin?.userId) && (
        <div className="font-size-11 mt-1">
          {membership?.admin?.id ? (
            <Link to={`/accounts/admins/${membership.admin.id}`}>
              {adminLabel}
            </Link>
          ) : (
            adminLabel
          )}
        </div>
      )}
    </div>
  )
}

const membershipColumns = () => [
  {
    dataField: "paymentId",
    formatter: formatTransaction,
    headerStyle: { width: "22%" },
    text: "Transaction",
  },
  {
    dataField: "membership",
    formatter: formatMembership,
    headerStyle: { width: "25%" },
    text: "Membership",
  },
  {
    dataField: "validity",
    formatter: formatValidity,
    headerStyle: { width: "28%" },
    text: "Validity",
  },
  {
    dataField: "source",
    formatter: formatSource,
    headerStyle: { width: "25%" },
    text: "Source & amount",
  },
]

export default membershipColumns
